import apiClient from '../client';
import {
  FloatingMenuResponse,
  FloatingMenuPatchRequest,
} from '../types/tenants.types';

export interface TenantMeta {
  id: string;
  action_menu: string;
  context_menu: string;
  selected: boolean;
  row_class: string | null;
  disabled: boolean;
  status: string;
}

export interface Tenant {
  tenant_id: string;
  created_by: string | null;
  version: number;
  updated_by: string | null;
  is_active: boolean;
  deleted_at: string | null;
  deleted_by: string | null;
  instance_state: string;
  code: string;
  display_name: string;
  parent_tenant_id: string | null;
  validation_messages: any | null;
  has_children: boolean;
  level: number;
  created_at: string;
  updated_at: string;
  __meta__: TenantMeta;
}

export interface ListAllTenantsResponse {
  data: Tenant[];
}

export interface UserMeta {
  id: string;
  selected: boolean;
  color: string;
}

export interface User {
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  __meta__: UserMeta;
}

export interface ListUsersResponse {
  header: {
    profile: {
      name: string;
    };
    bulk_select: {
      enabled: boolean;
      actions: any[];
    };
  };
  data: User[];
}

export const tenantsService = {
  /**
   * Get all available tenants
   */
  listAll: async (): Promise<Tenant[]> => {
    const response =
      await apiClient.get<ListAllTenantsResponse>('/tenants/list-all');
    return response.data.data;
  },

  /**
   * Get all users for current tenant with bulk select support
   */
  listUsers: async (selectionId?: string): Promise<ListUsersResponse> => {
    const params: any = {
      profile: 'select_users',
      per_page: 999,
      order: 'username',
    };

    if (selectionId) {
      params.selection_id = selectionId;
    }

    const response = await apiClient.get<ListUsersResponse>('/tenants/users', {
      params,
    });
    return response.data;
  },

  /**
   * Select multiple users
   */
  selectUsers: async (
    selectionId: string,
    userIds: string[]
  ): Promise<void> => {
    await apiClient.patch(
      `/tenants/users/bulk-select/${selectionId}/items/select-multiple`,
      userIds
    );
  },

  /**
   * Deselect multiple users
   */
  deselectUsers: async (
    selectionId: string,
    userIds: string[]
  ): Promise<void> => {
    await apiClient.patch(
      `/tenants/users/bulk-select/${selectionId}/items/clear-multiple`,
      userIds
    );
  },

  /**
   * Clear all selections
   */
  clearAllUserSelections: async (selectionId: string): Promise<void> => {
    await apiClient.delete(
      `/tenants/users/bulk-select/${selectionId}/clear-all`
    );
  },

  /**
   * Get current floating menu status
   * GET /v2/api/tenants/floating-menu
   */
  getFloatingMenuStatus: async (): Promise<FloatingMenuResponse> => {
    const response = await apiClient.get<FloatingMenuResponse>(
      '/tenants/floating-menu'
    );
    return response.data;
  },

  /**
   * Update floating menu status (partial update)
   * PATCH /v2/api/tenants/floating-menu
   */
  updateFloatingMenuStatus: async (
    request: FloatingMenuPatchRequest
  ): Promise<FloatingMenuResponse> => {
    const response = await apiClient.patch<FloatingMenuResponse>(
      '/tenants/floating-menu',
      request
    );
    return response.data;
  },

  /**
   * Reset floating menu status to default
   * DELETE /v2/api/tenants/floating-menu
   */
  resetFloatingMenuStatus: async (): Promise<FloatingMenuResponse> => {
    const response = await apiClient.delete<FloatingMenuResponse>(
      '/tenants/floating-menu'
    );
    return response.data;
  },
};
